import fetchWithAuth from "@/api/secureFetch";
import {
    DraftContextMeshApiError,
    parseDraftContextMeshResponse,
    type DraftContextMeshResponse
} from "./draftContextMeshApi";
import type { PersistedNewProductDraftState } from "./state";

export interface RefreshContextMeshPreviewRevision {
    draftId: number;
    draftVersion: number;
}

function endpoint(productId: number, suffix = ""): string {
    return `/api/products/${productId}/context-analysis/preview${suffix}`;
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === "object" && value !== null && !Array.isArray(value);
}

async function responsePayload(response: Response): Promise<unknown> {
    try {
        return await response.json();
    } catch {
        throw new DraftContextMeshApiError(
            response.status || 503,
            "INVALID_RESPONSE",
            "The Context Mesh service returned an invalid response."
        );
    }
}

function apiError(response: Response, payload: unknown): DraftContextMeshApiError {
    const error = isRecord(payload) && isRecord(payload.error) ? payload.error : payload;
    return new DraftContextMeshApiError(
        response.status,
        isRecord(error) && typeof error.code === "string" ? error.code : "ANALYSIS_UNAVAILABLE",
        isRecord(error) && typeof error.message === "string"
            ? error.message
            : "Context analysis is temporarily unavailable."
    );
}

export async function saveRefreshContextMeshPreview(
    token: string,
    productId: number,
    draftState: PersistedNewProductDraftState
): Promise<RefreshContextMeshPreviewRevision> {
    const response = await fetchWithAuth(endpoint(productId), token, {
        method: "PUT",
        body: JSON.stringify(draftState)
    });
    const payload = await responsePayload(response);
    if (!response.ok) throw apiError(response, payload);
    const preview = isRecord(payload) && isRecord(payload.preview) ? payload.preview : null;
    if (
        !preview ||
        typeof preview.draftId !== "number" ||
        !Number.isInteger(preview.draftId) ||
        preview.draftId <= 0 ||
        typeof preview.draftVersion !== "number" ||
        !Number.isInteger(preview.draftVersion) ||
        preview.draftVersion <= 0
    ) {
        throw new DraftContextMeshApiError(503, "INVALID_RESPONSE", "The Context Mesh service returned an invalid response.");
    }
    return { draftId: preview.draftId, draftVersion: preview.draftVersion };
}

export async function getRefreshContextMeshAnalysis(
    token: string,
    productId: number,
    _draftId: number
): Promise<DraftContextMeshResponse> {
    const response = await fetchWithAuth(endpoint(productId), token, {
        headers: { Accept: "application/json" }
    });
    const payload = await responsePayload(response);
    if (!response.ok) throw apiError(response, payload);
    try {
        return parseDraftContextMeshResponse(payload);
    } catch {
        throw new DraftContextMeshApiError(503, "INVALID_RESPONSE", "The Context Mesh service returned an invalid response.");
    }
}

export async function scheduleRefreshContextMeshAnalysis(
    token: string,
    productId: number,
    _draftId: number,
    draftVersion: number
): Promise<DraftContextMeshResponse> {
    const response = await fetchWithAuth(endpoint(productId, "/analyze"), token, {
        method: "POST",
        body: JSON.stringify({ draftVersion })
    });
    const payload = await responsePayload(response);
    if (!response.ok) throw apiError(response, payload);
    try {
        return parseDraftContextMeshResponse(payload);
    } catch {
        throw new DraftContextMeshApiError(503, "INVALID_RESPONSE", "The Context Mesh service returned an invalid response.");
    }
}

export async function discardRefreshContextMeshPreview(token: string, productId: number): Promise<void> {
    const response = await fetchWithAuth(endpoint(productId), token, { method: "DELETE" });
    if (response.status === 404) return;
    if (!response.ok) {
        const payload = await responsePayload(response);
        throw apiError(response, payload);
    }
}
